// pages/ReturnsPolicy.jsx
import React, {useState, useEffect, useLayoutEffect} from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { FiPackage, FiMail, FiPhone, FiMapPin, FiCheckCircle, FiRefreshCw, FiShield, FiHeart } from 'react-icons/fi';
import { GiReturnArrow, GiTakeMyMoney, GiEarthAmerica } from 'react-icons/gi';

const ReturnsPolicy = () => {
    const [isScrolled, setIsScrolled] = useState(false);


    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const returnJourney = [
        {
            step: "01",
            title: "Initiate Your Return",
            description: "Access your account dashboard or contact our dedicated concierge team",
            icon: <FiRefreshCw className="w-8 h-8" />,
            duration: "Within 30 days",
            color: "from-muted-sage/20 to-muted-sage/5"
        },
        {
            step: "02",
            title: "Receive Luxury Packaging",
            description: "We'll send our signature embossed return kit with prepaid shipping",
            icon: <FiPackage className="w-8 h-8" />,
            duration: "1-2 business days",
            color: "from-champagne-gold/20 to-champagne-gold/5"
        },
        {
            step: "03",
            title: "Secure & Protected",
            description: "Your items are insured and tracked throughout the return journey",
            icon: <FiShield className="w-8 h-8" />,
            duration: "Fully insured",
            color: "from-muted-sage/20 to-muted-sage/5"
        },
        {
            step: "04",
            title: "Refund Processed",
            description: "Receive confirmation and refund to your original payment method",
            icon: <FiCheckCircle className="w-8 h-8" />,
            duration: "5-10 business days",
            color: "from-champagne-gold/20 to-champagne-gold/5"
        }
    ];

    const luxuryPillars = [
        {
            icon: <FiShield className="w-6 h-6" />,
            title: "Guaranteed Protection",
            description: "All returns are fully insured and tracked from door to door"
        },
        {
            icon: <GiEarthAmerica className="w-6 h-6" />,
            title: "Sustainable Practice",
            description: "Zero-waste policy with ethical product redistribution"
        },
        {
            icon: <FiHeart className="w-6 h-6" />,
            title: "Community Impact",
            description: "Unopened products donated to women's shelters and charities"
        },
        {
            icon: <GiTakeMyMoney className="w-6 h-8" />,
            title: "Instant Refund Status",
            description: "Real-time updates on your refund processing timeline"
        }
    ];

    const sections = [
        {
            title: "The KROMAIC Return Promise",
            content: [
                "At KROMAIC, we believe true luxury is defined by confidence and trust. Our Return Policy is not just a process, but a commitment to your complete satisfaction.",
                "Every KROMAIC product is crafted with meticulous attention to detail, and we extend that same care to ensure your return experience is seamless, transparent, and exceptional."
            ]
        },
        {
            title: "Eligibility & Conditions",
            content: [
                "• Returns accepted within 30 days of delivery for a full refund",
                "• Products must be unopened, unused, in original packaging with all seals intact",
                "• Gift sets must include all original components and documentation",
                "• For hygiene standards, opened skincare cannot be returned unless defective"
            ],
            note: "All returns undergo our 7-point quality inspection to ensure authenticity and condition."
        },
        {
            title: "Defective or Damaged Items",
            content: [
                "In the rare event you receive a damaged or defective product, our priority is immediate resolution.",
                "Contact our concierge team within 48 hours of delivery for expedited processing. We'll arrange a prepaid luxury return kit and priority replacement shipping at no cost to you."
            ],
            highlight: "Luxury replacements are processed within 24 hours of inspection."
        },
        {
            title: "International Returns",
            content: [
                "As a global luxury brand, we've streamlined international returns for our worldwide clientele.",
                "International customers receive detailed customs documentation and our team handles all paperwork. Return shipping costs are calculated based on your location, with duties and taxes clearly outlined before processing."
            ],
            note: "We recommend using our recommended carriers for optimal tracking and security."
        },
        {
            title: "Refund Timeline & Methods",
            content: [
                "Refunds are processed within 5-10 business days after we receive and inspect your return.",
                "All refunds are issued to your original payment method. For international orders, processing may take an additional 2-3 days due to currency conversion.",
                "Shipping costs are non-refundable unless the return results from our error or a defective product."
            ]
        },
        {
            title: "Sustainable Returns Initiative",
            content: [
                "At KROMAIC, luxury and sustainability are inseparable. Our Sustainable Returns Program ensures no product goes to waste.",
                "Unopened products are professionally sanitized and donated to women's shelters. Packaging is recycled through our zero-waste program, and all materials are either repurposed or responsibly recycled."
            ],
            highlight: "100% of our packaging is recyclable or compostable."
        }
    ];

    return (
        <div className="min-h-screen bg-pearl-white">
            {/* Fixed Navbar */}
            <div
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                    isScrolled
                        ? 'bg-white/95 backdrop-blur-xl shadow-soft'
                        : 'bg-transparent'
                }`}
            >
                <Navbar />
            </div>

            {/* Hero Section - Luxury Editorial */}
            <section id="hero" className="pt-24 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-pearl-white via-soft-ivory to-nude-beige/30">
                    <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
                        <div className="absolute top-20 right-20 w-96 h-96 rounded-full bg-champagne-gold blur-3xl" />
                        <div className="absolute bottom-20 left-20 w-64 h-64 rounded-full bg-muted-sage blur-3xl" />
                    </div>
                </div>

                <div className="container-luxury relative z-10">
                    <div className="max-w-5xl mx-auto pt-20 pb-32">
                        <motion.div
                            initial={{ opacity: 0, y: 60 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1.2, ease: "easeOut" }}
                            className="text-center"
                        >
                            <div className="inline-flex items-center gap-3 mb-8">
                                <GiReturnArrow className="w-8 h-8 text-champagne-gold" />
                                <span className="font-poppins text-sm uppercase tracking-widest text-muted-sage">
                  Luxury Assurance
                </span>
                            </div>

                            <h1 className="font-playfair text-6xl md:text-8xl lg:text-9xl text-deep-charcoal mb-8 leading-none">
                                Return & Refund
                            </h1>

                            <div className="max-w-2xl mx-auto">
                                <p className="font-poppins text-2xl text-charcoal-60 leading-relaxed mb-12">
                                    Where luxury meets peace of mind. Your complete satisfaction,
                                    guaranteed with the KROMAIC standard of excellence.
                                </p>
                            </div>

                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 1, delay: 0.8 }}
                                className="inline-flex items-center gap-6"
                            >
                                <div className="h-px w-16 bg-champagne-gold" />
                                <span className="font-cormorant text-xl text-champagne-gold italic">
                  The KROMAIC Promise
                </span>
                                <div className="h-px w-16 bg-champagne-gold" />
                            </motion.div>
                        </motion.div>
                    </div>
                </div>

                {/* Decorative Border */}
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-nude-beige to-transparent" />
            </section>

            {/* Quick Concierge CTA */}
            <section id="concierge" className="section-spacing -mt-16 relative z-20">
                <div className="container-luxury">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="max-w-4xl mx-auto"
                    >
                        <div className="bg-gradient-to-br from-white to-pearl-white rounded-3xl shadow-luxe p-8 md:p-12
              border border-nude-beige/50 backdrop-blur-sm"
                        >
                            <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
                                <div>
                                    <h2 className="font-cormorant text-3xl md:text-4xl text-deep-charcoal mb-4">
                                        Luxury Return Concierge
                                    </h2>
                                    <p className="font-poppins text-charcoal-60">
                                        Our dedicated team is here to guide you through every step
                                    </p>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-4">
                                    <motion.button
                                        whileHover={{ scale: 1.02, y: -2 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="px-8 py-4 bg-deep-charcoal text-white font-poppins rounded-full
                      hover:bg-deep-charcoal/90 transition-all duration-300 flex items-center gap-3
                      shadow-soft hover:shadow-lift"
                                    >
                                        <FiMail className="w-5 h-5" />
                                        Start Return Online
                                    </motion.button>

                                    <motion.button
                                        whileHover={{ scale: 1.02, y: -2 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="px-8 py-4 bg-transparent border-2 border-nude-beige text-deep-charcoal
                      font-poppins rounded-full hover:border-muted-sage hover:text-muted-sage
                      transition-all duration-300 flex items-center gap-3"
                                    >
                                        <FiPhone className="w-5 h-5" />
                                        Contact Concierge
                                    </motion.button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Luxury Return Journey */}
            <section id="journey" className="section-spacing bg-soft-ivory/50">
                <div className="container-luxury">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 1 }}
                        className="mb-20"
                    >
                        <div className="text-center mb-16">
                            <div className="inline-flex items-center gap-4 mb-6">
                                <div className="h-px w-12 bg-champagne-gold" />
                                <span className="font-poppins text-sm uppercase tracking-widest text-champagne-gold">
                  The Journey
                </span>
                                <div className="h-px w-12 bg-champagne-gold" />
                            </div>

                            <h2 className="font-playfair text-5xl md:text-6xl text-deep-charcoal mb-8">
                                Seamless Return Experience
                            </h2>
                            <p className="font-poppins text-xl text-charcoal-60 max-w-3xl mx-auto">
                                From initiation to refund, every step reflects our commitment to luxury service
                            </p>
                        </div>

                        <div className="relative">
                            {/* Connecting Line - Desktop Only */}
                            <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-gradient-to-r from-muted-sage/20 via-champagne-gold/20 to-muted-sage/20 -translate-y-1/2 hidden lg:block" />

                            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                                {returnJourney.map((step, index) => (
                                    <motion.div
                                        key={step.step}
                                        initial={{ opacity: 0, y: 40 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.6, delay: index * 0.1 }}
                                        className="relative"
                                    >
                                        {/* Step Number Background */}
                                        <div className="absolute -top-6 -left-6 font-playfair text-7xl text-nude-beige font-bold
                      opacity-30 z-0">
                                            {step.step}
                                        </div>

                                        <div className={`bg-gradient-to-br ${step.color} backdrop-blur-sm rounded-2xl 
                      p-8 relative z-10 border border-white/50 shadow-soft hover:shadow-lift 
                      transition-all duration-500 h-full`}
                                        >
                                            <div className="flex items-center gap-4 mb-6">
                                                <div className="w-16 h-16 rounded-xl bg-white/80 flex items-center justify-center
                          text-muted-sage shadow-soft">
                                                    {step.icon}
                                                </div>
                                                <div>
                                                    <div className="font-poppins text-xs uppercase tracking-widest text-champagne-gold">
                                                        Step {step.step}
                                                    </div>
                                                    <div className="font-poppins text-sm text-charcoal-40">
                                                        {step.duration}
                                                    </div>
                                                </div>
                                            </div>

                                            <h3 className="font-cormorant text-2xl text-deep-charcoal mb-4">
                                                {step.title}
                                            </h3>
                                            <p className="font-poppins text-charcoal-60 leading-relaxed">
                                                {step.description}
                                            </p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Policy Content - Luxury Editorial */}
            <section id="policy" className="section-spacing">
                <div className="container-luxury">
                    <div className="max-w-5xl mx-auto">
                        <div className="bg-gradient-to-b from-white to-pearl-white rounded-3xl shadow-soft
              p-8 md:p-16 border border-nude-beige/30"
                        >
                            {/* Section Header */}
                            <motion.div
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                                className="mb-16 text-center"
                            >
                                <div className="inline-flex items-center gap-4 mb-8">
                                    <div className="h-px w-8 bg-muted-sage" />
                                    <span className="font-poppins text-sm uppercase tracking-widest text-muted-sage">
                    Policy Details
                  </span>
                                    <div className="h-px w-8 bg-muted-sage" />
                                </div>

                                <h2 className="font-playfair text-5xl text-deep-charcoal mb-8">
                                    The KROMAIC Return Philosophy
                                </h2>
                            </motion.div>

                            {/* Policy Sections */}
                            <div className="space-y-20">
                                {sections.map((section, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 40 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, amount: 0.2 }}
                                        transition={{ duration: 0.6, delay: index * 0.1 }}
                                        className="relative"
                                    >
                                        {/* Decorative Number */}
                                        <div className="absolute -left-6 top-0 font-playfair text-6xl text-nude-beige
                      font-bold -z-10">
                                            {String(index + 1).padStart(2, '0')}
                                        </div>

                                        <div className="pl-12">
                                            <div className="flex items-center gap-4 mb-8">
                                                <div className="h-px w-12 bg-champagne-gold" />
                                                <h3 className="font-cormorant text-3xl md:text-4xl text-deep-charcoal">
                                                    {section.title}
                                                </h3>
                                            </div>

                                            <div className="space-y-6">
                                                {Array.isArray(section.content) ? (
                                                    section.content.map((paragraph, pIndex) => (
                                                        <motion.p
                                                            key={pIndex}
                                                            initial={{ opacity: 0 }}
                                                            whileInView={{ opacity: 1 }}
                                                            viewport={{ once: true }}
                                                            transition={{ duration: 0.5, delay: index * 0.1 + pIndex * 0.1 }}
                                                            className="font-poppins text-lg text-charcoal-60 leading-relaxed"
                                                        >
                                                            {paragraph}
                                                        </motion.p>
                                                    ))
                                                ) : (
                                                    <p className="font-poppins text-lg text-charcoal-60 leading-relaxed">
                                                        {section.content}
                                                    </p>
                                                )}
                                            </div>

                                            {section.note && (
                                                <motion.div
                                                    initial={{ opacity: 0 }}
                                                    whileInView={{ opacity: 1 }}
                                                    viewport={{ once: true }}
                                                    transition={{ duration: 0.5, delay: 0.4 }}
                                                    className="mt-8 p-6 bg-muted-sage/5 rounded-xl border-l-4 border-muted-sage"
                                                >
                                                    <p className="font-poppins text-charcoal-60 italic">
                                                        {section.note}
                                                    </p>
                                                </motion.div>
                                            )}

                                            {section.highlight && (
                                                <motion.div
                                                    initial={{ opacity: 0 }}
                                                    whileInView={{ opacity: 1 }}
                                                    viewport={{ once: true }}
                                                    transition={{ duration: 0.5, delay: 0.4 }}
                                                    className="mt-8 inline-flex items-center gap-3 px-6 py-3 bg-champagne-gold/10
                            rounded-full"
                                                >
                                                    <FiCheckCircle className="w-5 h-5 text-champagne-gold" />
                                                    <span className="font-poppins text-champagne-gold">
                            {section.highlight}
                          </span>
                                                </motion.div>
                                            )}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Luxury Pillars */}
            <section id="pillars" className="section-spacing bg-gradient-to-b from-soft-ivory to-pearl-white">
                <div className="container-luxury">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                    >
                        <div className="text-center mb-16">
                            <h2 className="font-playfair text-5xl text-deep-charcoal mb-8">
                                Pillars of Our Promise
                            </h2>
                            <p className="font-poppins text-xl text-charcoal-60 max-w-3xl mx-auto">
                                Four fundamental principles that define the KROMAIC return experience
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {luxuryPillars.map((pillar, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    whileHover={{ y: -8 }}
                                    className="bg-white rounded-2xl p-8 shadow-soft hover:shadow-lift
                    transition-all duration-500 border border-nude-beige/30"
                                >
                                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-muted-sage/10 to-champagne-gold/10
                    flex items-center justify-center text-muted-sage mb-6">
                                        {pillar.icon}
                                    </div>

                                    <h3 className="font-cormorant text-2xl text-deep-charcoal mb-4">
                                        {pillar.title}
                                    </h3>
                                    <p className="font-poppins text-charcoal-60">
                                        {pillar.description}
                                    </p>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Contact & Address - Luxury Card */}
            <section id="contact" className="section-spacing">
                <div className="container-luxury">
                    <div className="max-w-6xl mx-auto">
                        <div className="bg-gradient-to-br from-white to-pearl-white rounded-3xl shadow-luxe
              overflow-hidden border border-nude-beige/30"
                        >
                            <div className="grid grid-cols-1 lg:grid-cols-2">
                                {/* Address Card */}
                                <motion.div
                                    initial={{ opacity: 0, x: -40 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8 }}
                                    className="p-12 bg-gradient-to-br from-soft-ivory to-nude-beige/30"
                                >
                                    <div className="flex items-center gap-4 mb-8">
                                        <div className="w-12 h-12 rounded-xl bg-white/80 flex items-center justify-center
                      text-deep-charcoal shadow-soft">
                                            <FiMapPin className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <div className="font-poppins text-sm uppercase tracking-widest text-muted-sage">
                                                Return Center
                                            </div>
                                            <h3 className="font-cormorant text-3xl text-deep-charcoal">
                                                Luxury Address
                                            </h3>
                                        </div>
                                    </div>

                                    <div className="space-y-6">
                                        <address className="font-poppins text-lg text-charcoal-60 not-italic leading-relaxed">
                                            KROMAIC Returns Concierge<br />
                                            123 Luxury Lane, Suite 450<br />
                                            New York, NY 10001<br />
                                            United States
                                        </address>

                                        <div className="pt-8 border-t border-nude-beige">
                                            <p className="font-poppins text-sm text-charcoal-40 mb-4">
                                                Hours of Operation
                                            </p>
                                            <p className="font-poppins text-charcoal-60">
                                                Monday - Friday: 9:00 AM - 6:00 PM EST<br />
                                                Saturday: 10:00 AM - 4:00 PM EST<br />
                                                Sunday: Closed
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>

                                {/* Contact Card */}
                                <motion.div
                                    initial={{ opacity: 0, x: 40 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8, delay: 0.2 }}
                                    className="p-12 bg-white"
                                >
                                    <div className="flex items-center gap-4 mb-8">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-muted-sage/10 to-champagne-gold/10
                      flex items-center justify-center text-deep-charcoal">
                                            <FiPhone className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <div className="font-poppins text-sm uppercase tracking-widest text-champagne-gold">
                                                Concierge Support
                                            </div>
                                            <h3 className="font-cormorant text-3xl text-deep-charcoal">
                                                Immediate Assistance
                                            </h3>
                                        </div>
                                    </div>

                                    <div className="space-y-8">
                                        <div>
                                            <p className="font-poppins text-lg text-charcoal-60 mb-4">
                                                Our luxury concierge team is dedicated to providing personalized support
                                            </p>

                                            <div className="space-y-4">
                                                <a
                                                    href="tel:+18885551234"
                                                    className="group flex items-center gap-4 p-4 rounded-xl bg-soft-ivory hover:bg-nude-beige/50
                            transition-all duration-300"
                                                >
                                                    <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center
                            text-muted-sage group-hover:text-muted-sage/80">
                                                        <FiPhone className="w-5 h-5" />
                                                    </div>
                                                    <div>
                                                        <div className="font-poppins font-medium text-deep-charcoal">
                                                            +1 (888) 555-1234
                                                        </div>
                                                        <div className="font-poppins text-sm text-charcoal-40">
                                                            Priority luxury line
                                                        </div>
                                                    </div>
                                                </a>

                                                <a
                                                    href="mailto:concierge@kromaic.com"
                                                    className="group flex items-center gap-4 p-4 rounded-xl bg-soft-ivory hover:bg-nude-beige/50
                            transition-all duration-300"
                                                >
                                                    <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center
                            text-champagne-gold group-hover:text-champagne-gold/80">
                                                        <FiMail className="w-5 h-5" />
                                                    </div>
                                                    <div>
                                                        <div className="font-poppins font-medium text-deep-charcoal">
                                                            concierge@kromaic.com
                                                        </div>
                                                        <div className="font-poppins text-sm text-charcoal-40">
                                                            Response within 2 hours
                                                        </div>
                                                    </div>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Closing Statement */}
            <section className="section-spacing">
                <div className="container-luxury">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                        className="max-w-4xl mx-auto text-center"
                    >
                        <div className="mb-12">
                            <GiReturnArrow className="w-16 h-16 text-champagne-gold mx-auto mb-8" />
                            <h2 className="font-playfair text-5xl text-deep-charcoal mb-8">
                                Your Satisfaction, Our Priority
                            </h2>
                            <p className="font-poppins text-xl text-charcoal-60 leading-relaxed">
                                At KROMAIC, we believe luxury is defined not only by what we create but by the
                                confidence we instill. Our Return Policy is a testament to our commitment to
                                excellence in every aspect of your experience with us.
                            </p>
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="inline-flex items-center gap-6"
                        >
                            <div className="h-px w-12 bg-gradient-to-r from-transparent to-champagne-gold" />
                            <span className="font-cormorant text-2xl text-champagne-gold italic">
                The KROMAIC Standard
              </span>
                            <div className="h-px w-12 bg-gradient-to-l from-transparent to-champagne-gold" />
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ReturnsPolicy;